import { portalText, portalLocale } from "../../../../i18n/portal.js";
/**
 * The order book's slice of the console's data seam — P12-03.
 *
 * It lives in the route area rather than in `src/api/` for the reason `channel-sandbox.ts`
 * lives beside `channel-view.ts`: the views, the wire decoding and the fixture for one
 * screen belong to the unit that owns that screen, and `src/api/gateway.ts` grows one line
 * (`extends OrderBookGateway`) rather than ninety.
 *
 * MONEY CROSSES HERE AND NOWHERE ELSE IN THE CONSOLE. The order book's wire carries major
 * units as a JSON number — a compatibility contract with `order-system-client.ts`, not a
 * choice — and every WeTalk surface renders `Money`, which is integer minor units plus a
 * currency (ADR 0014). `moneyOfWire` is the one conversion, and `src/money.ts`'s
 * `formatMoney` is what draws it. No screen in this directory sees a float.
 */
import { money, type CurrencyCode, type Money } from "@wetalk/domain";

import {
  MalformedResponseError,
  readArray,
  readBoolean,
  readRecord,
  readText,
  readWholeNumber,
} from "../../../../api/decode.js";
import {
  customerOrderAwaitsPayment,
  customerOrderPaymentLineOf,
  customerOrderPaymentOfWire,
  isCustomerOrderPaymentDisplayState,
  type CustomerOrderPaymentDisplayStateView,
  type CustomerOrderPaymentView,
} from "../../../../api/slice/customer-order-payment.js";

/** The status flow, as the console renders it. */
export const CUSTOMER_ORDER_STATUS = [
  "received",
  "preparing",
  "ready",
  "out_for_delivery",
  "delivered",
  "cancelled",
] as const;

export type CustomerOrderStatusView = (typeof CUSTOMER_ORDER_STATUS)[number];

export function isCustomerOrderStatus(value: string): value is CustomerOrderStatusView {
  return (CUSTOMER_ORDER_STATUS as readonly string[]).includes(value);
}

/**
 * The Orders tab's status chips, plus the "all" that is not a status, and — plan-005 OP-36, OD-P6
 * (a) — "awaiting_payment", which is not a status either: a `received` order whose online payment
 * has not arrived. It is outside "New" (the kitchen's "To accept") until it is paid, marked paid
 * or switched to hand-over. It sits right before "received" because it is the step before it.
 */
export const CUSTOMER_ORDER_FILTER = ["all", "awaiting_payment", ...CUSTOMER_ORDER_STATUS] as const;
export type CustomerOrderFilter = (typeof CUSTOMER_ORDER_FILTER)[number];

export function isCustomerOrderFilter(value: string): value is CustomerOrderFilter {
  return (CUSTOMER_ORDER_FILTER as readonly string[]).includes(value);
}

/**
 * The `status` the API is asked for, for a chip. "Awaiting payment" and "New" are both `received`
 * on the server; OD-P6 splits them by payment state, which the API filters on too — see
 * {@link serverAwaitsPaymentOfFilter}.
 */
export function serverStatusOfFilter(filter: CustomerOrderFilter): CustomerOrderStatusView | null {
  if (filter === "all") return null;
  if (filter === "awaiting_payment") return "received";
  return filter;
}

/**
 * The `awaits_payment` the API is asked for, for a chip (plan-005 review item 6): "Awaiting payment"
 * asks for `true`, "New" for `false`, every other chip for no payment filter. The SERVER splits the
 * two, so each chip gets full pages — splitting one `received` page in the browser left a chip with
 * a short (or empty) first page while its orders sat on the next.
 */
export function serverAwaitsPaymentOfFilter(filter: CustomerOrderFilter): "true" | "false" | null {
  if (filter === "awaiting_payment") return "true";
  if (filter === "received") return "false";
  return null;
}

/** Whether a row belongs under a chip. OD-P6 (a): an order awaiting payment is not "New". */
export function matchesCustomerOrderFilter(row: CustomerOrderRowView, filter: CustomerOrderFilter): boolean {
  if (filter === "all") return true;
  const awaiting = customerOrderRowAwaitsPayment(row);
  if (filter === "awaiting_payment") return awaiting;
  if (filter === "received") return row.status === "received" && !awaiting;
  return row.status === filter;
}

/**
 * OD-P6 (a) on a row: a `received` order whose online payment has not arrived. Once somebody has
 * started it anyway (after the warning), it is the kitchen's and stays in its status column.
 */
export function customerOrderRowAwaitsPayment(row: Pick<CustomerOrderRowView, "status" | "paymentState">): boolean {
  return row.status === "received" && customerOrderAwaitsPayment(row.paymentState);
}

export interface OrderLineView {
  readonly label: string;
  readonly quantity: number;
  /** Size, crust and toppings, already joined — the card renders one line, not five. */
  readonly modification: string;
  readonly notes: string | null;
  readonly lineTotal: Money;
}

export interface CustomerOrderRowView {
  readonly reference: string;
  readonly status: CustomerOrderStatusView;
  readonly placedAtText: string;
  readonly partyName: string | null;
  readonly partyPhone: string | null;
  /** `2× Margherita (large), 1× Coke` — what the table's "What they ordered" column shows. */
  readonly itemSummary: string;
  readonly fulfilmentText: string;
  readonly total: Money;
  readonly paymentText: string;
  /** `cash`, `card_on_delivery`, `online`, or `null` when the caller did not say. */
  readonly paymentMethod: string | null;
  readonly paid: boolean;
  /**
   * The order's payment state (plan-005 OP-36, IC-56) — the API's `payment_state`. It replaces
   * review #111's interim `awaitsOnlinePayment` ("no link is ever sent, collect on hand-over"): a
   * link is sent now, so an unpaid online order is awaiting it, or its payment did not complete.
   */
  readonly paymentState: CustomerOrderPaymentDisplayStateView;
  /** The latest payment attempt was a test (the placeholder page). */
  readonly paymentIsTest: boolean;
  /**
   * How the VoiceAgent that took it said callers pay — `[]` takes no payment on the phone, `null`
   * not stated. "Use another method" offers the hand-over methods in it (both when `null`).
   */
  readonly acceptedPaymentMethod: readonly string[] | null;
  /**
   * The VoiceAgent that took it (review 112, review 120): its conversation's, else the one its shop
   * was opened for. `null` for a counter or API order in a shop no VoiceAgent opened.
   */
  readonly voiceAgentId: string | null;
  /** That VoiceAgent's name — the Orders table's "Agent" column (review 120). */
  readonly voiceAgentName: string | null;
  /**
   * The conversation the order came from (plan-015 G10: the list row carries it too, so its phone
   * button reaches the call). `null` for a counter or API order: the row draws no phone button.
   */
  readonly conversationId: string | null;
  /**
   * G10: whether that conversation has a recording this person may play — `true` or `false` for a
   * holder of `recording.play` (Owner, Admin), and `null` for everybody else, because the API omits
   * the field rather than tell a Member or a Viewer that a recording exists. Only `true` draws play;
   * otherwise the phone button opens the conversation in History.
   */
  readonly recordingAvailable: boolean | null;
  /**
   * The VoiceAgent that took the call takes no payment on the phone (its AgentVersion's
   * `acceptedPaymentMethod` is `[]`, package K): the shop settles payment itself, so an unpaid
   * order says so instead of "Unpaid" or "collect on delivery".
   */
  readonly takesNoPaymentInConversation: boolean;
}

/** What an unpaid order from a VoiceAgent that takes no payment on the phone reads (K follow-up). */
export const NO_PAYMENT_IN_CONVERSATION_TEXT = () => (portalText("operations", "no_payment_taken_on_the_call_the_shop_settles_it_8660b9dc"));

/** What kind of event a "What happened" entry is (customer-portal review #112). */
export type CustomerOrderTimelineKindView =
  | "placed"
  | "moved"
  | "cancelled"
  | "undone"
  | "paid"
  | "ticket_printed";

export interface CustomerOrderTimelineEntryView {
  readonly atText: string;
  readonly kind: CustomerOrderTimelineKindView;
  /** The event in words — "Sent to the kitchen · at the counter screen" — never a bare time. */
  readonly text: string;
  /** The status the order was in after this event, when the event moved it. */
  readonly status: CustomerOrderStatusView | null;
}

/** The one-step undo on offer (review #123): back to `toStatus`, until `untilText`. */
export interface CustomerOrderUndoView {
  readonly fromStatus: CustomerOrderStatusView;
  readonly toStatus: CustomerOrderStatusView;
  readonly untilText: string;
}

export interface CustomerOrderDetailView extends CustomerOrderRowView {
  readonly line: readonly OrderLineView[];
  readonly subtotal: Money;
  readonly discount: Money;
  readonly deliveryFee: Money;
  readonly couponCode: string | null;
  readonly address: string | null;
  readonly addressNote: string | null;
  readonly note: string | null;
  readonly etaMinute: number;
  /** The caller's phone and address were withheld from this Member or Viewer (review #133). */
  readonly callerDetailWithheld: boolean;
  readonly timeline: readonly CustomerOrderTimelineEntryView[];
  readonly undo: CustomerOrderUndoView | null;
  /** The online payment, in full (IC-52a); `null` when none was ever attempted or recorded. */
  readonly payment: CustomerOrderPaymentView | null;
}

export interface CustomerOrderPageView {
  readonly row: readonly CustomerOrderRowView[];
  readonly nextCursor: string | null;
}

export interface CustomerOrderSummaryView {
  /** `null` while the workspace has no shop yet: the book opens with its first order (#97). */
  readonly shopId: string | null;
  readonly shopName: string;
  readonly counterAccessKeySet: boolean;
  readonly orderCount: number;
  readonly activeCount: number;
  readonly deliveredCount: number;
  readonly cancelledCount: number;
  readonly gross: Money;
  /**
   * What the shop has been paid (review #123; IC-56 `takenMinorOf`): paid online net of refunds,
   * marked paid, or delivered and settled on hand-over. Never an unpaid online order.
   */
  readonly taken: Money;
  readonly takenCount: number;
  /** Whether this person sees callers' phone numbers and addresses (review #133). */
  readonly callerDetailVisible: boolean;
}

/** What a rotation returns. The key is shown once, like an API key. */
export interface CounterAccessKeyView {
  readonly shopId: string;
  readonly counterAccessKey: string;
  readonly notice: string;
}

/** A shop's two wait times, in whole minutes (2026-09-30). */
export interface ShopWaitTimeView {
  readonly deliveryMinute: number;
  readonly pickupMinute: number;
}

/** What "Print ticket" produces. */
export interface OrderTicketView {
  readonly printedAtText: string;
  readonly plainText: string;
}

/** The slice of `ConsoleGateway` the Orders tab and Order detail need. */
export interface OrderBookGateway {
  listCustomerOrders(request: {
    readonly voiceAgentId: string;
    readonly filter: CustomerOrderFilter;
    readonly search: string;
    readonly cursor: string | null;
  }): Promise<CustomerOrderPageView>;
  getCustomerOrderSummary(voiceAgentId: string): Promise<CustomerOrderSummaryView>;
  getCustomerOrder(reference: string): Promise<CustomerOrderDetailView>;
  /** Advance one step along the flow. The console never names a target status. */
  advanceCustomerOrder(reference: string): Promise<CustomerOrderDetailView>;
  /**
   * Cancel from any status that is not final, saying why (review #123). `refundPayment` answers
   * FR-PAY-12's question for a paid online order — refund it in the same act, or keep the money —
   * and is `null` for every other order, where there is nothing to ask.
   */
  cancelCustomerOrder(reference: string, reason: string, refundPayment: boolean | null): Promise<CustomerOrderDetailView>;
  /** Reverse the order's latest move, once, within the undo window (review #123). */
  undoCustomerOrderMove(reference: string): Promise<CustomerOrderDetailView>;
  /**
   * Record that the money was taken another way — cash at the door, card on hand-over. The API
   * refuses it while a payment link is active (`customer_order_payment_link_active`), so the
   * console offers "Cancel link" first and "Mark paid" only once no link is live (FR-PAY-10).
   */
  markCustomerOrderPaid(reference: string): Promise<CustomerOrderDetailView>;
  printOrderTicket(reference: string): Promise<OrderTicketView>;
  /** Revokes the old counter link and returns the new one, once. */
  rotateCounterAccessKey(shopId: string): Promise<CounterAccessKeyView>;
  /** 2026-09-30: the wait times the voice agent tells callers, delivery and pickup, in minutes. */
  shopWaitTime(shopId: string): Promise<ShopWaitTimeView>;
  setShopWaitTime(shopId: string, wait: ShopWaitTimeView): Promise<ShopWaitTimeView>;
  /** Where "Export CSV" points. A URL, not a fetch — the browser downloads it. */
  customerOrderExportUrl(voiceAgentId: string): string;
  /** Where "Open shop screen" points. The key is in the FRAGMENT, never in the path. */
  shopScreenUrl(counterAccessKey: string): string;
}

// ---------------------------------------------------------------------------------------
// Wire decoding. One direction, and the only place a float becomes `Money`.
// ---------------------------------------------------------------------------------------

/**
 * A major-unit wire number to `Money`.
 *
 * `Math.round(value * 100)` is the same conversion `packages/order-book/src/money.ts` does at
 * the server's boundary, for the same reason: the product of a double and 100 is off by at
 * most one ulp at every magnitude money reaches, so one round recovers the intended cent.
 */
export function moneyOfWire(value: unknown, at: string, currency: CurrencyCode): Money {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new MalformedResponseError(at, portalText("operations", "a_finite_amount_2f532678"), value);
  }
  return money(Math.round(value * 100), currency);
}

function currencyOf(row: Readonly<Record<string, unknown>>, at: string): CurrencyCode {
  const raw = readText(row["currency"], `${at}.currency`);
  if (raw !== "EUR") {
    throw new MalformedResponseError(`${at}.currency`, portalText("operations", "eur_which_is_what_this_console_renders_d1c896bd"), raw);
  }
  return raw;
}

function statusOf(raw: unknown, at: string): CustomerOrderStatusView {
  const value = readText(raw, at);
  if (!isCustomerOrderStatus(value)) {
    throw new MalformedResponseError(at, portalText("operations", "a_documented_order_status_acdcb953"), value);
  }
  return value;
}

function nullableText(raw: unknown): string | null {
  return typeof raw === "string" && raw.length > 0 ? raw : null;
}

/** `21 Sep, 18:42` — short, because it sits under a reference in a dense table. */
/**
 * `Today 19:42`, `Yesterday 20:14`, or `23 Sept, 04:23` further back — the design's row time.
 *
 * A shop owner reads the order book during the day it describes, so the day is a word
 * until it is not.
 */
export function formatPlacedAt(iso: string, now: Date = new Date()): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return iso;
  const clock = new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(when);
  const dayOf = (at: Date): string => `${String(at.getFullYear())}-${String(at.getMonth())}-${String(at.getDate())}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (dayOf(when) === dayOf(now)) return portalText("operations", "today_value_98ab9355", { v0: String(clock) });
  if (dayOf(when) === dayOf(yesterday)) return portalText("operations", "yesterday_value_7b96d8f6", { v0: String(clock) });
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(when);
}

/**
 * `1× Margherita (large), 2× Coke` — what the table's "What they ordered" column shows. The
 * size is named in brackets because it is the one modification that changes the price.
 */
export function itemSummaryOf(
  line: readonly { readonly quantity: number; readonly label: string; readonly size: string | null }[],
): string {
  return line
    .map(
      (one) =>
        `${String(one.quantity)}× ${one.label}${one.size === null ? "" : ` (${one.size.toLowerCase()})`}`,
    )
    .join(", ");
}

function modificationOf(line: Readonly<Record<string, unknown>>): string {
  const size = nullableText(line["size_label"]);
  const crust = nullableText(line["crust_label"]);
  const topping = readArray(line["topping_labels"] ?? [], "orderLine.topping_labels").map(
    (one) => `+ ${readText(one, "orderLine.topping_labels[]")}`,
  );
  return [size, crust, ...topping].filter((one): one is string => one !== null).join(" · ");
}

/** `true` when the wire's `accepted_payment_method` is `[]` — no payment on the phone (K follow-up). */
function takesNoPaymentInConversationOf(row: Readonly<Record<string, unknown>>, at: string): boolean {
  const raw = row["accepted_payment_method"];
  if (raw === null || raw === undefined) return false;
  return readArray(raw, `${at}.accepted_payment_method`).length === 0;
}

/**
 * What a `not_online` order's payment says — the rules from before online payment, unchanged:
 * "Paid", the method, the no-payment line, or "Unpaid". An online order never reaches here: its
 * words come from its payment state (`customerOrderPaymentLineOf`), which is what undid review
 * #111's interim "Not paid · no payment link was sent".
 */
export function notOnlinePaymentTextOf(input: {
  readonly paid: boolean;
  readonly paymentMethod: string | null;
  readonly takesNoPaymentInConversation: boolean;
}): string {
  if (input.paid) return portalText("operations", "paid_dc9d4584");
  // Package K's follow-up: the VoiceAgent neither offered nor recorded a method, so the order is
  // the shop's to settle — not "Unpaid", and never "collect on delivery".
  if (input.paymentMethod === null && input.takesNoPaymentInConversation) return NO_PAYMENT_IN_CONVERSATION_TEXT();
  switch (input.paymentMethod) {
    case "cash":
      return portalText("operations", "cash_758ec54e");
    case "card_on_delivery":
      return portalText("operations", "card_on_delivery_78c3f220");
    default:
      return portalText("operations", "unpaid_50cc12f6");
  }
}

/**
 * The row's payment state: the API's `payment_state` (IC-52a).
 *
 * An API that predates plan-005 does not send it. No payment attempt can exist there, so the state
 * is `customerOrderPaymentStateOf` over the facts that API has — `paid`, the method and no attempt:
 * paid → `marked_paid`, an unpaid online order → `online_payment_not_completed` (IC-56 rule 5),
 * anything else → `not_online`. That is the same derivation with no attempt, not a substitute; a
 * value that IS sent must be one of the eight states, or the row is malformed.
 */
export function paymentStateOfWire(
  row: Readonly<Record<string, unknown>>,
  at: string,
  paid: boolean,
  paymentMethod: string | null,
): CustomerOrderPaymentDisplayStateView {
  const raw = row["payment_state"];
  if (raw === undefined) {
    if (paid) return "marked_paid";
    return paymentMethod === "online" ? "online_payment_not_completed" : "not_online";
  }
  const value = readText(raw, `${at}.payment_state`);
  if (!isCustomerOrderPaymentDisplayState(value)) {
    throw new MalformedResponseError(`${at}.payment_state`, portalText("operations", "a_documented_payment_state_a9ea48ff"), value);
  }
  return value;
}

/** `accepted_payment_method` as the wire carries it: `null` when not stated. */
function acceptedPaymentMethodOf(row: Readonly<Record<string, unknown>>, at: string): readonly string[] | null {
  const raw = row["accepted_payment_method"];
  if (raw === null || raw === undefined) return null;
  return readArray(raw, `${at}.accepted_payment_method`).map((one) =>
    readText(one, `${at}.accepted_payment_method[]`),
  );
}

/** "1 order", "3 orders" (review #102). */
export function customerOrderCountText(count: number): string {
  return `${String(count)} ${count === 1 ? portalText("operations", "order_cce55e43") : portalText("operations", "orders_96584038")}`;
}

/** How a status reads in a sentence on the Order screen. */
export const CUSTOMER_ORDER_STATUS_LABEL: Readonly<Record<CustomerOrderStatusView, string>> = {
  get received() { return portalText("operations", "new_6403f2b7"); },
  get preparing() { return portalText("operations", "in_the_kitchen_d4d0b524"); },
  get ready() { return portalText("operations", "ready_20c7c552"); },
  get out_for_delivery() { return portalText("operations", "out_for_delivery_111fb997"); },
  get delivered() { return portalText("operations", "delivered_eea956cd"); },
  get cancelled() { return portalText("operations", "cancelled_a1bf92ef"); },
};

/** What a move did, in the words a shop uses ("Sent to the kitchen"), by where it arrived. */
function moveTextOf(to: CustomerOrderStatusView, pickup: boolean): string {
  switch (to) {
    case "received":
      return portalText("operations", "back_to_new_6c750ae2");
    case "preparing":
      return portalText("operations", "sent_to_the_kitchen_67017d4e");
    case "ready":
      return portalText("operations", "marked_ready_0970e852");
    case "out_for_delivery":
      return portalText("operations", "out_for_delivery_111fb997");
    case "delivered":
      return pickup ? portalText("operations", "collected_5b0f7590") : portalText("operations", "delivered_eea956cd");
    case "cancelled":
      return portalText("operations", "cancelled_a1bf92ef");
  }
}

/** Who did it, as a suffix: the counter tablet, a person in the console, or the agent. */
function actorTextOf(actorKind: string | null, kind: CustomerOrderTimelineKindView): string {
  if (actorKind === "counter") return portalText("operations", "at_the_counter_screen_a0b2642b");
  if (actorKind === "person" && kind !== "ticket_printed") return portalText("operations", "from_the_console_5c0396d2");
  if (actorKind === "operator") return portalText("operations", "by_wetalk_support_960bb16a");
  return "";
}

/**
 * One "What happened" entry in words (review #112: it used to show only a time). Pure, so the
 * sandbox and the API render the same sentence.
 */
export function timelineTextOf(entry: {
  readonly kind: CustomerOrderTimelineKindView;
  readonly fromStatus: CustomerOrderStatusView | null;
  readonly toStatus: CustomerOrderStatusView | null;
  readonly actorKind: string | null;
  readonly reason: string | null;
  readonly pickup: boolean;
  readonly fromConversation: boolean;
}): string {
  switch (entry.kind) {
    case "placed":
      return entry.fromConversation ? portalText("operations", "order_placed_by_the_agent_on_the_call_92b4f9a2") : portalText("operations", "order_placed_bb5d68da");
    case "paid":
      return portalText("operations", "marked_paid_31242153");
    case "ticket_printed":
      return portalText("operations", "kitchen_ticket_printed_9b28abc9");
    case "cancelled":
      return portalText("operations", "cancelledvaluevalue_41862fef", { v0: String(entry.reason === null ? "" : `: ${entry.reason}`), v1: String(actorTextOf(entry.actorKind, entry.kind)) });
    case "undone": {
      const undone = entry.fromStatus === null ? portalText("operations", "the_last_change_b8898f11") : `“${moveTextOf(entry.fromStatus, entry.pickup)}”`;
      const back = entry.toStatus === null ? "" : portalText("operations", "back_to_value_6081b73d", { v0: String(CUSTOMER_ORDER_STATUS_LABEL[entry.toStatus].toLowerCase()) });
      return portalText("operations", "undid_valuevaluevalue_49d13167", { v0: String(undone), v1: String(back), v2: String(actorTextOf(entry.actorKind, entry.kind)) });
    }
    case "moved":
      return `${entry.toStatus === null ? portalText("operations", "moved_0fc28db0") : moveTextOf(entry.toStatus, entry.pickup)}${actorTextOf(entry.actorKind, entry.kind)}`;
  }
}

const TIMELINE_KIND: readonly CustomerOrderTimelineKindView[] = [
  "placed",
  "moved",
  "cancelled",
  "undone",
  "paid",
  "ticket_printed",
];

function timelineKindOf(raw: unknown, at: string): CustomerOrderTimelineKindView {
  const value = readText(raw, at);
  const found = TIMELINE_KIND.find((one) => one === value);
  if (found === undefined) throw new MalformedResponseError(at, portalText("operations", "a_documented_timeline_event_49180b6d"), value);
  return found;
}

function nullableStatusOf(raw: unknown, at: string): CustomerOrderStatusView | null {
  return raw === null || raw === undefined ? null : statusOf(raw, at);
}

function fulfilmentTextOf(party: Readonly<Record<string, unknown>>): string {
  const address = nullableText(party["address"]);
  if (nullableText(party["fulfillment"]) === "delivery") {
    return address === null ? portalText("operations", "delivery_9631af52") : portalText("operations", "delivery_value_11da5735", { v0: String(address) });
  }
  return portalText("operations", "collection_30c54a96");
}

export function customerOrderRowOfWire(raw: unknown, at: string): CustomerOrderRowView {
  const row = readRecord(raw, at);
  const currency = currencyOf(row, at);
  const party = readRecord(row["customer"], `${at}.customer`);
  const line = readArray(row["items"], `${at}.items`).map((one) => readRecord(one, `${at}.items[]`));
  const paid = readBoolean(row["paid"], `${at}.paid`);
  const paymentMethod = nullableText(party["payment"]);
  const takesNoPaymentInConversation = takesNoPaymentInConversationOf(row, at);
  const paymentState = paymentStateOfWire(row, at, paid, paymentMethod);
  const paymentIsTest =
    row["payment_is_test"] === undefined ? false : readBoolean(row["payment_is_test"], `${at}.payment_is_test`);

  return {
    reference: readText(row["id"], `${at}.id`),
    status: statusOf(row["status"], `${at}.status`),
    placedAtText: formatPlacedAt(readText(row["created_at"], `${at}.created_at`)),
    partyName: nullableText(party["name"]),
    partyPhone: nullableText(party["phone"]),
    itemSummary: itemSummaryOf(
      line.map((one) => ({
        quantity: readWholeNumber(one["quantity"], `${at}.items[].quantity`),
        label: readText(one["label"], `${at}.items[].label`),
        size: nullableText(one["size_label"]),
      })),
    ),
    fulfilmentText: fulfilmentTextOf(party),
    total: moneyOfWire(row["total"], `${at}.total`, currency),
    paymentText: customerOrderPaymentLineOf({
      state: paymentState,
      isTest: paymentIsTest,
      paymentMethod,
      notOnlineText: notOnlinePaymentTextOf({ paid, paymentMethod, takesNoPaymentInConversation }),
    }),
    paymentMethod,
    paid,
    paymentState,
    paymentIsTest,
    acceptedPaymentMethod: acceptedPaymentMethodOf(row, at),
    voiceAgentId: nullableText(row["voice_agent_id"]),
    voiceAgentName: nullableText(row["voice_agent_name"]),
    conversationId: nullableText(row["conversation_id"]),
    recordingAvailable:
      row["recording_available"] === undefined ? null : readBoolean(row["recording_available"], `${at}.recording_available`),
    takesNoPaymentInConversation,
  };
}

export function customerOrderDetailOfWire(raw: unknown, at: string, now: Date = new Date()): CustomerOrderDetailView {
  const row = readRecord(raw, at);
  const currency = currencyOf(row, at);
  const coupon = row["coupon"] === null ? null : readRecord(row["coupon"], `${at}.coupon`);
  const line = readArray(row["items"], `${at}.items`).map((one) => readRecord(one, `${at}.items[]`));
  const party = readRecord(row["customer"], `${at}.customer`);
  const rowView = customerOrderRowOfWire(raw, at);
  const payment = customerOrderPaymentOfWire(row["payment"], `${at}.payment`, now);
  // The detail's `payment.state` is the same derivation as the row's `payment_state`; when the
  // block is there it is what the page shows, so the tag and the Payment card cannot disagree.
  const paymentState = payment === null ? rowView.paymentState : payment.state;
  const paymentIsTest = payment === null ? rowView.paymentIsTest : payment.isTest;

  return {
    ...rowView,
    paymentState,
    paymentIsTest,
    paymentText: customerOrderPaymentLineOf({
      state: paymentState,
      isTest: paymentIsTest,
      paymentMethod: rowView.paymentMethod,
      notOnlineText: notOnlinePaymentTextOf(rowView),
    }),
    line: line.map((one) => ({
      label: readText(one["label"], `${at}.items[].label`),
      quantity: readWholeNumber(one["quantity"], `${at}.items[].quantity`),
      modification: modificationOf(one),
      notes: nullableText(one["notes"]),
      lineTotal: moneyOfWire(one["line_total"], `${at}.items[].line_total`, currency),
    })),
    subtotal: moneyOfWire(row["subtotal"], `${at}.subtotal`, currency),
    discount:
      coupon === null
        ? money(0, currency)
        : moneyOfWire(coupon["discount"], `${at}.coupon.discount`, currency),
    deliveryFee: moneyOfWire(row["delivery_fee"] ?? 0, `${at}.delivery_fee`, currency),
    couponCode: coupon === null ? null : readText(coupon["code"], `${at}.coupon.code`),
    address: nullableText(party["address"]),
    addressNote: nullableText(row["address_note"]),
    note: nullableText(row["note"]),
    etaMinute: readWholeNumber(row["eta_minutes"], `${at}.eta_minutes`),
    callerDetailWithheld: row["caller_detail_withheld"] === true,
    timeline: readArray(row["timeline"], `${at}.timeline`).map((one) => {
      const entry = readRecord(one, `${at}.timeline[]`);
      const kind = timelineKindOf(entry["kind"], `${at}.timeline[].kind`);
      const toStatus = nullableStatusOf(entry["to_status"], `${at}.timeline[].to_status`);
      return {
        atText: formatPlacedAt(readText(entry["at"], `${at}.timeline[].at`)),
        kind,
        status: toStatus,
        text: timelineTextOf({
          kind,
          fromStatus: nullableStatusOf(entry["from_status"], `${at}.timeline[].from_status`),
          toStatus,
          actorKind: nullableText(entry["actor_kind"]),
          reason: nullableText(entry["reason"]),
          pickup: nullableText(party["fulfillment"]) !== "delivery",
          fromConversation: nullableText(row["conversation_id"]) !== null,
        }),
      };
    }),
    undo: undoOfWire(row["undo"], `${at}.undo`),
    payment,
  };
}

function undoOfWire(raw: unknown, at: string): CustomerOrderUndoView | null {
  if (raw === null || raw === undefined) return null;
  const undo = readRecord(raw, at);
  const until = new Date(readText(undo["until"], `${at}.until`));
  return {
    fromStatus: statusOf(undo["from_status"], `${at}.from_status`),
    toStatus: statusOf(undo["to_status"], `${at}.to_status`),
    untilText: new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { hour: "2-digit", minute: "2-digit", hour12: false }).format(until),
  };
}

export function customerOrderSummaryOfWire(raw: unknown, at: string): CustomerOrderSummaryView {
  const row = readRecord(raw, at);
  const currency = currencyOf(row, at);
  return {
    shopId: row["shop_id"] === null ? null : readText(row["shop_id"], `${at}.shop_id`),
    shopName: readText(row["shop_name"], `${at}.shop_name`),
    counterAccessKeySet: readBoolean(
      row["counter_access_key_set"],
      `${at}.counter_access_key_set`,
    ),
    orderCount: readWholeNumber(row["order_count"], `${at}.order_count`),
    activeCount: readWholeNumber(row["active_count"], `${at}.active_count`),
    deliveredCount: readWholeNumber(row["delivered_count"], `${at}.delivered_count`),
    cancelledCount: readWholeNumber(row["cancelled_count"], `${at}.cancelled_count`),
    gross: moneyOfWire(row["gross"], `${at}.gross`, currency),
    taken: moneyOfWire(row["taken"], `${at}.taken`, currency),
    takenCount: readWholeNumber(row["taken_count"], `${at}.taken_count`),
    callerDetailVisible: readBoolean(row["caller_detail_visible"], `${at}.caller_detail_visible`),
  };
}

/**
 * The client-side search the design's box performs.
 *
 * "Order number, name or phone", over the page already loaded. It is deliberately NOT a
 * server query: there is no documented search parameter, and a box that silently searched
 * only the current page while looking like it searched everything would be the worse of the
 * two lies. The screen says which it is.
 */
export function matchesCustomerOrderSearch(row: CustomerOrderRowView, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (needle === "") return true;
  return (
    row.reference.toLowerCase().includes(needle) ||
    (row.partyName ?? "").toLowerCase().includes(needle) ||
    (row.partyPhone ?? "").includes(needle)
  );
}
